import { z } from 'zod'
import { parseFieldValue, type FieldDefinition } from './custom-fields.ts'
import { MEMBERSHIP_STATUSES, normalizePhoneE164 } from './people.ts'

/**
 * CSV import: what a column can mean, and what one row becomes.
 *
 * The mapping is explicit and stored — never inferred at commit time — because
 * an import that guessed differently on the retry than on the dry run is worse
 * than one that refused. `guessMapping` only seeds the UI; a human confirms it.
 */

/** Targets a CSV column can be mapped to. `custom:<fieldId>` is dynamic. */
export const IMPORT_TARGETS = [
  'firstName',
  'lastName',
  'middleName',
  'nickname',
  'prefix',
  'suffix',
  'gender',
  'birthdate',
  'anniversary',
  'maritalStatus',
  'membershipStatus',
  'campus',
  'email',
  'phone',
  'street',
  'street2',
  'city',
  'state',
  'postalCode',
  'country',
  'tags',
] as const
export type ImportTarget = (typeof IMPORT_TARGETS)[number] | `custom:${string}`

/** Column header → target, or null for "ignore this column". */
export type ColumnMapping = Record<string, ImportTarget | null>

const TARGET_LABELS: Record<(typeof IMPORT_TARGETS)[number], string> = {
  firstName: 'First name',
  lastName: 'Last name',
  middleName: 'Middle name',
  nickname: 'Nickname',
  prefix: 'Prefix',
  suffix: 'Suffix',
  gender: 'Gender',
  birthdate: 'Birthdate',
  anniversary: 'Anniversary',
  maritalStatus: 'Marital status',
  membershipStatus: 'Membership status',
  campus: 'Campus',
  email: 'Email',
  phone: 'Phone',
  street: 'Street',
  street2: 'Street line 2',
  city: 'City',
  state: 'State / region',
  postalCode: 'Postcode',
  country: 'Country',
  tags: 'Tags (comma-separated)',
}

export const importTargetLabel = (target: ImportTarget): string =>
  target.startsWith('custom:')
    ? 'Custom field'
    : (TARGET_LABELS[target as keyof typeof TARGET_LABELS] ?? target)

/** Header spellings seen in the wild, normalized to a comparable key. */
const normalizeHeader = (header: string) => header.toLowerCase().replace(/[^a-z0-9]/g, '')

const HEADER_ALIASES: Record<string, (typeof IMPORT_TARGETS)[number]> = {
  firstname: 'firstName',
  first: 'firstName',
  givenname: 'firstName',
  forename: 'firstName',
  lastname: 'lastName',
  last: 'lastName',
  surname: 'lastName',
  familyname: 'lastName',
  middlename: 'middleName',
  middle: 'middleName',
  nickname: 'nickname',
  preferredname: 'nickname',
  goesby: 'nickname',
  prefix: 'prefix',
  title: 'prefix',
  suffix: 'suffix',
  gender: 'gender',
  sex: 'gender',
  birthdate: 'birthdate',
  birthday: 'birthdate',
  dob: 'birthdate',
  dateofbirth: 'birthdate',
  anniversary: 'anniversary',
  weddinganniversary: 'anniversary',
  maritalstatus: 'maritalStatus',
  marriedstatus: 'maritalStatus',
  membershipstatus: 'membershipStatus',
  status: 'membershipStatus',
  membership: 'membershipStatus',
  membershiptype: 'membershipStatus',
  // Planning Center and Breeze exports (Phase 11 migration tooling).
  nameprefix: 'prefix',
  namesuffix: 'suffix',
  homeemail: 'email',
  personalemail: 'email',
  mobilephonenumber: 'phone',
  cellphonenumber: 'phone',
  homephonenumber: 'phone',
  homephone: 'phone',
  homeaddressstreetline1: 'street',
  homeaddressline1: 'street',
  homeaddressstreet: 'street',
  homeaddressstreetline2: 'street2',
  homeaddressline2: 'street2',
  homeaddresscity: 'city',
  homeaddressstate: 'state',
  homeaddresszip: 'postalCode',
  homeaddresszipcode: 'postalCode',
  homeaddresspostalcode: 'postalCode',
  homeaddresscountry: 'country',
  campus: 'campus',
  location: 'campus',
  site: 'campus',
  email: 'email',
  emailaddress: 'email',
  emailaddress1: 'email',
  primaryemail: 'email',
  phone: 'phone',
  phonenumber: 'phone',
  mobile: 'phone',
  cell: 'phone',
  cellphone: 'phone',
  mobilephone: 'phone',
  address: 'street',
  street: 'street',
  streetaddress: 'street',
  address1: 'street',
  addressline1: 'street',
  address2: 'street2',
  addressline2: 'street2',
  city: 'city',
  town: 'city',
  state: 'state',
  province: 'state',
  region: 'state',
  county: 'state',
  postalcode: 'postalCode',
  postcode: 'postalCode',
  zip: 'postalCode',
  zipcode: 'postalCode',
  country: 'country',
  tags: 'tags',
  labels: 'tags',
  groups: 'tags',
}

/**
 * The two systems churches most often arrive from. Recognised by columns only
 * they export, so the mapping screen can say "This is a Planning Center export"
 * and the importer can read Breeze's US-style dates without being told.
 */
export type ImportSource = 'planning_center' | 'breeze'

export const IMPORT_SOURCE_LABELS: Record<ImportSource, string> = {
  planning_center: 'Planning Center',
  breeze: 'Breeze',
}

export function detectImportSource(headers: string[]): ImportSource | null {
  const keys = new Set(headers.map(normalizeHeader))
  if (keys.has('breezeid')) return 'breeze'
  if (
    keys.has('personid') &&
    (keys.has('homeemail') || keys.has('householdid') || keys.has('mobilephonenumber'))
  )
    return 'planning_center'
  return null
}

/**
 * Membership words other systems use, read as ours: Planning Center's
 * "Regular Attender", Breeze's "Attender", "Guest", "Former Member"… Anything
 * else is left as written and reported, not guessed.
 */
const STATUS_SYNONYMS: Record<string, (typeof MEMBERSHIP_STATUSES)[number]> = {
  member: 'member',
  members: 'member',
  'active member': 'member',
  'covenant member': 'member',
  'church member': 'member',
  regular: 'regular',
  attender: 'regular',
  attendee: 'regular',
  'regular attender': 'regular',
  'regular attendee': 'regular',
  participant: 'regular',
  visitor: 'visitor',
  guest: 'visitor',
  'first time guest': 'visitor',
  'first-time guest': 'visitor',
  'first time visitor': 'visitor',
  prospect: 'visitor',
  inactive: 'inactive',
  former: 'inactive',
  'former member': 'inactive',
  'former attender': 'inactive',
  archived: 'inactive',
  moved: 'inactive',
  'moved away': 'inactive',
}

export function normalizeMembershipStatus(
  value: string,
): (typeof MEMBERSHIP_STATUSES)[number] | null {
  return STATUS_SYNONYMS[value.trim().toLowerCase().replace(/\s+/g, ' ')] ?? null
}

/**
 * Seeds the mapping UI. Custom fields match on their label or key, so a church
 * that exported from us can re-import without re-mapping.
 */
export function guessMapping(
  headers: string[],
  customFields: { id: string; key: string; label: string }[] = [],
): ColumnMapping {
  const byCustom = new Map<string, string>()
  for (const field of customFields) {
    byCustom.set(normalizeHeader(field.label), field.id)
    byCustom.set(normalizeHeader(field.key), field.id)
  }

  const mapping: ColumnMapping = {}
  const used = new Set<string>()
  for (const header of headers) {
    const key = normalizeHeader(header)
    const custom = byCustom.get(key)
    const target = custom ? (`custom:${custom}` as ImportTarget) : HEADER_ALIASES[key]
    // One target per file: two "Email" columns would otherwise both win, and
    // the second would silently replace the first.
    mapping[header] = target && !used.has(target) ? target : null
    if (target && !used.has(target)) used.add(target)
  }
  return mapping
}

// ---- one row -----------------------------------------------------------------

export interface MappedRow {
  firstName: string
  lastName: string
  middleName?: string
  nickname?: string
  prefix?: string
  suffix?: string
  gender?: string
  birthdate?: string
  anniversary?: string
  maritalStatus?: string
  membershipStatus?: string
  campus?: string
  email?: string
  phone?: string
  address: {
    street?: string
    street2?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
  }
  tags: string[]
  /** Custom field values by field id, still raw. */
  custom: Record<string, string>
}

const isCustomTarget = (target: ImportTarget): target is `custom:${string}` =>
  target.startsWith('custom:')

/** The MappedRow keys that hold a plain string. */
type ScalarTarget = Exclude<
  (typeof IMPORT_TARGETS)[number],
  'street' | 'street2' | 'city' | 'state' | 'postalCode' | 'country' | 'tags'
>

const assignScalar = (mapped: MappedRow, target: ScalarTarget, value: string): void => {
  mapped[target] = value
}

export function mapRow(headers: string[], row: string[], mapping: ColumnMapping): MappedRow {
  const mapped: MappedRow = { firstName: '', lastName: '', address: {}, tags: [], custom: {} }

  headers.forEach((header, index) => {
    const target = mapping[header]
    const value = (row[index] ?? '').trim()
    if (!target || value === '') return

    // A type guard, not a bare `startsWith`: the latter does not narrow a
    // template-literal union, leaving `custom:${string}` in the switch below.
    if (isCustomTarget(target)) {
      mapped.custom[target.slice('custom:'.length)] = value
      return
    }
    switch (target) {
      case 'street':
      case 'street2':
      case 'city':
      case 'state':
      case 'postalCode':
      case 'country':
        mapped.address[target] = value
        return
      case 'tags':
        mapped.tags = value
          .split(/[,;]/)
          .map((tag) => tag.trim())
          .filter((tag) => tag !== '')
        return
      case 'membershipStatus':
        // "Regular Attender" is ours "regular"; an unknown word stays as written
        // so validation can name it.
        mapped.membershipStatus = normalizeMembershipStatus(value) ?? value
        return
      default:
        // The remaining targets are all the plain string fields of MappedRow;
        // the switch above has removed every other case.
        assignScalar(mapped, target, value)
    }
  })

  return mapped
}

// ---- validation ---------------------------------------------------------------

export interface RowIssue {
  /** 1-based, counting the header as row 1 — what the spreadsheet shows. */
  row: number
  column: string
  message: string
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * `new Date('2000-02-30')` does not throw — it rolls over to March 1st. Only a
 * round trip catches a day that does not exist, and an imported birthday
 * quietly moved by a day is exactly the kind of wrong nobody reports.
 */
const isRealDate = (iso: string): boolean => {
  const parsed = new Date(`${iso}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === iso
}
/** US-style dates are the most common non-ISO input; anything else is refused. */
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/

/**
 * Normalizes a date cell, or returns null if it cannot be read confidently.
 * Ambiguity is refused rather than guessed: 03/04/2019 is March or April
 * depending on the country, and a birthday off by a month is worse than a
 * rejected row.
 */
export function parseImportDate(value: string, assume: 'us' | 'iso' = 'iso'): string | null {
  const trimmed = value.trim()
  if (ISO_DATE.test(trimmed)) return isRealDate(trimmed) ? trimmed : null

  const us = US_DATE.exec(trimmed)
  if (us && assume === 'us') {
    const [, month, day, year] = us
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return isRealDate(iso) ? iso : null
  }
  return null
}

export interface ValidationContext {
  /** Live custom fields, for `custom:` columns. */
  customFields: FieldDefinition[]
  /** Campus names the org actually has, lowercased. */
  campusNames: Set<string>
  dateFormat: 'us' | 'iso'
}

/**
 * Validates one mapped row. Returns every problem, not the first — a person
 * fixing a spreadsheet wants the whole list of what is wrong with row 412.
 */
export function validateRow(
  mapped: MappedRow,
  rowNumber: number,
  ctx: ValidationContext,
): RowIssue[] {
  const issues: RowIssue[] = []
  const add = (column: string, message: string) => issues.push({ row: rowNumber, column, message })

  if (mapped.firstName.trim() === '') add('First name', 'Required')
  if (mapped.lastName.trim() === '') add('Last name', 'Required')

  if (mapped.email && !z.email().safeParse(mapped.email).success) {
    add('Email', `Not a valid email address: ${mapped.email}`)
  }
  if (mapped.phone && normalizePhoneE164(mapped.phone) === null) {
    add('Phone', `Not a valid phone number: ${mapped.phone}`)
  }
  for (const [field, label] of [
    ['birthdate', 'Birthdate'],
    ['anniversary', 'Anniversary'],
  ] as const) {
    const value = mapped[field]
    if (value && parseImportDate(value, ctx.dateFormat) === null) {
      add(label, `Not a date this import can read: ${value}`)
    }
  }
  if (
    mapped.membershipStatus &&
    !MEMBERSHIP_STATUSES.includes(
      mapped.membershipStatus.toLowerCase() as (typeof MEMBERSHIP_STATUSES)[number],
    )
  ) {
    add('Membership status', `Unknown status: ${mapped.membershipStatus}`)
  }
  if (mapped.campus && !ctx.campusNames.has(mapped.campus.toLowerCase())) {
    add('Campus', `No campus named ${mapped.campus}`)
  }

  for (const [fieldId, raw] of Object.entries(mapped.custom)) {
    const field = ctx.customFields.find((candidate) => candidate.id === fieldId)
    if (!field) {
      add('Custom field', 'This field no longer exists')
      continue
    }
    // Option values arrive as labels in a CSV, not ids — resolve before parsing.
    const value =
      field.type === 'select' || field.type === 'multiselect'
        ? (field.options.find((option) => option.label.toLowerCase() === raw.toLowerCase())?.id ??
          raw)
        : raw
    const parsed = parseFieldValue(field, value)
    if (!parsed.ok) add(field.label, parsed.error)
  }

  return issues
}

/** How a matched row is treated. Stored on the job so a retry behaves the same. */
export const IMPORT_MODES = ['create-only', 'update-existing'] as const
export type ImportMode = (typeof IMPORT_MODES)[number]

export const importMappingSchema = z.object({
  mapping: z.record(z.string(), z.string().nullable()),
  mode: z.enum(IMPORT_MODES).default('create-only'),
  dateFormat: z.enum(['us', 'iso']).default('iso'),
})
export type ImportMappingInput = z.infer<typeof importMappingSchema>

/**
 * The key an import matches on to decide create-vs-update: a normalized email,
 * else a normalized phone. Name matching is deliberately excluded — two
 * Maria Garcias in one church is normal, and merging them on import is a
 * data-loss bug that nobody notices for months.
 */
export function dedupeKey(mapped: MappedRow): string | null {
  if (mapped.email) return `email:${mapped.email.trim().toLowerCase()}`
  const phone = mapped.phone ? normalizePhoneE164(mapped.phone) : null
  return phone ? `phone:${phone}` : null
}
